import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import _ from 'lodash';

import {
    DefaultButton,
    PrimaryButton,
    Panel,
    PanelType,
    Separator,
    Stack
} from '@fluentui/react';

import { RootState } from '../../../../../redux/application/redux-store';
import { Area } from '../../../../../api-client';
import { TRANSLATION_KEYS } from './localization/translation-keys';

import { FormProvider, IAIComboBox, IAITextField } from '../../../../../common/components/IAIForm';

interface FormValuesProps extends Area {
    departmentKeys: string[];
}

interface IFormDrawer {
    area?: Area;
    disabled?: boolean;
    drawerVisible?: boolean;
    onDrawerConfirm: (dataItem: Area) => void;
    onDrawerDismiss: () => void;
}

const FormDrawer: React.FunctionComponent<IFormDrawer> = ({
    area,
    disabled,
    drawerVisible,
    onDrawerConfirm,
    onDrawerDismiss
}) => {
    const {
        t: translate
    } = useTranslation();

    //#region Form-Configuration

    const formDataSchema = Yup.object().shape({
        displayName: Yup.string().required(translate(TRANSLATION_KEYS.TitleErrorMessage)),
        departmentKeys: Yup.array().min(1, translate(TRANSLATION_KEYS.DepartmentErrorMessage)).required(translate(TRANSLATION_KEYS.DepartmentErrorMessage))
    });

    const defaultValues = useMemo(
        () => ({
            displayName: area?.displayName || '',
            departmentKeys: _.map(area?.departments, entry => entry.key) || []
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [area]
    );

    const methods = useForm<FormValuesProps>({ resolver: yupResolver(formDataSchema), defaultValues });

    const {
        reset,
        handleSubmit,
        formState: { isSubmitting }
    } = methods;

    useEffect(() => {
        reset(defaultValues);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [area]);

    //#endregion

    const _handleDismiss = () => {
        reset(defaultValues);
        onDrawerDismiss();
    }

    const iaiSystem = useSelector((state: RootState) => state.system);

    const _handleSubmit = (data: FormValuesProps) => {
        let areaData = {
            ...area,
            ...data,
            departments: _.filter(iaiSystem.departments, entry => _.some(data.departmentKeys, key => entry.key === key))
        };

        onDrawerConfirm(areaData);
    }

    const disableCondition = disabled || !iaiSystem.departmentsInitialized || isSubmitting;
    const departmentOptions = _.map(iaiSystem.departments, entry => ({ key: entry.key!, text: entry.text! }));

    return (
        <FormProvider methods={methods}>
            <Panel
                type={PanelType.medium}
                headerText={translate(TRANSLATION_KEYS.Heading)}
                isOpen={drawerVisible}
                isBlocking={true}
                isFooterAtBottom={true}
                onDismiss={_handleDismiss}
                onRenderFooterContent={() => (
                    <div className="text-end">
                        <DefaultButton
                            disabled={disableCondition}
                            iconProps={{ iconName: 'Cancel' }}
                            styles={{ root: { marginRight: 10 } }}
                            onClick={_handleDismiss}
                        >
                            {translate(TRANSLATION_KEYS.DismissText)}
                        </DefaultButton>
                        <PrimaryButton
                            type="submit"
                            disabled={disableCondition}
                            iconProps={{ iconName: 'CheckMark' }}
                            onClick={handleSubmit(_handleSubmit)}
                        >
                            {translate(TRANSLATION_KEYS.ConfirmText)}
                        </PrimaryButton>
                    </div>
                )}
            >
                <Separator />

                <Stack tokens={{ childrenGap: 15 }}>
                    <IAITextField
                        name="displayName"
                        label={translate(TRANSLATION_KEYS.TitleLabel)}
                        placeholder={translate(TRANSLATION_KEYS.TitlePlaceholder)}
                        required
                        disabled={disableCondition}
                    />

                    <IAIComboBox
                        name="departmentKeys"
                        label={translate(TRANSLATION_KEYS.DepartmentLabel)}
                        placeholder={translate(TRANSLATION_KEYS.DepartmentPlaceholder)}
                        autoComplete="on"
                        multiSelect
                        required
                        options={departmentOptions}
                    />
                </Stack>
            </Panel>
        </FormProvider>
    );
}

export default FormDrawer;